import { useNavigate } from 'react-router-dom';
import type { RequestCard } from '@shared/types.ts';
import { Focusable } from '@/ui/Focusable.tsx';
import { StatusBadge } from '@/ui/StatusBadge.tsx';
import { Strip } from '@/ui/Strip.tsx';

/**
 * What the household has asked for lately.
 *
 * A DIFFERENT SHAPE FROM THE POSTER ROWS, on purpose. Recently Added is a
 * fact about the library; this is a fact about the people in it — "Tom asked
 * for this" is a conversation and "new" is not. A wide card with the
 * requester's name on it says that; a poster identical to the ones above it
 * would say nothing and just make the home screen longer.
 *
 * HANDED ITS DATA rather than fetching it. The home endpoint has always
 * included these — it builds them alongside everything else on the screen —
 * so this component asking for them again was a second round-trip for bytes
 * already in the response that rendered it.
 *
 * It also does something the request feature needed and did not have:
 * somebody who asks for a film currently gets a confirmation and then
 * silence. Seeing it sitting on the home screen, and later seeing it turn
 * green, is most of what makes asking again feel worth it.
 */
export function RequestRow({ requests }: { requests?: RequestCard[] }) {
  const navigate = useNavigate();
  const rows = requests ?? [];
  if (!rows.length) return null;

  return (
    <section className="libe-row">
      <h2 className="libe-title">Recently requested</h2>
      <Strip className="req-strip">
        {rows.map((r) => (
          <Card key={r.id} req={r} onOpen={() => navigate(`/search?q=${encodeURIComponent(r.title)}`)} />
        ))}
      </Strip>
    </section>
  );
}

function Card({ req, onOpen }: { req: RequestCard; onOpen(): void }) {
  const waiting = req.state !== 'available';

  return (
    <Focusable
      id={`req-card-${req.id}`}
      className="req-tile"
      onSelect={onOpen}
      backdrop={req.backdropUrl ?? req.posterUrl}
    >
      {/* The backdrop is the card's surface rather than a picture on it, so
          the text sits in the scene the way it does on the detail screen. */}
      {req.backdropUrl && (
        <div className="req-tile-bg" style={{ backgroundImage: `url(${req.backdropUrl})` }} />
      )}

      <div className="req-tile-body">
        {req.year && <div className="req-tile-year">{req.year}</div>}
        <div className="req-tile-title">{req.title}</div>

        <div className="req-tile-who">
          {req.requestedByAvatar
            ? <img src={req.requestedByAvatar} alt="" className="req-tile-face" />
            : <span className="req-tile-face req-tile-initial">{req.requestedBy.slice(0, 1)}</span>}
          <span className="req-tile-name">{req.requestedBy}</span>
        </div>

        <div className={`req-tile-state ${waiting ? 'res-wait-text' : 'res-have-text'}`}>
          {req.state === 'available' ? 'Available'
            : req.state === 'partial' ? 'Partly here'
              : req.state === 'processing' ? 'Downloading' : 'Requested'}
        </div>
      </div>

      {req.posterUrl && <img src={req.posterUrl} alt="" className="req-tile-poster" loading="lazy" />}
      <StatusBadge state={req.state} />
    </Focusable>
  );
}
